import { useEffect, useState } from 'react';
import { FileText, X } from 'lucide-react';
import { Heading } from './Heading';
import { spacingClass } from '@/utils/spacing';
import { useI18n } from '@/components/_vs/i18n/I18nProvider';

const FALLBACK_LABEL = { it: 'Vedi il menu', en: 'View menu' };

/**
 * Un solo pulsante "Vedi il menu" — niente più copertine/pillole lingua: il
 * PDF giusto è scelto in automatico dalla lingua corrente del sito
 * (useI18n). Desktop: overlay in-page con iframe (chiusura su backdrop/Esc,
 * scroll bloccato). Mobile: il tap apre il PDF nel viewer nativo del
 * browser (target="_blank") invece dell'overlay — un iframe dentro un
 * overlay fisso è inaffidabile su Safari/Chrome mobile (barra indirizzi che
 * mangia spazio, pinch-zoom in conflitto con lo scroll dell'overlay) mentre
 * il viewer nativo ha già zoom/condividi/stampa/salva pronti.
 * @param {{
 *   title?: string, subtitle?: string, buttonLabel?: string,
 *   items?: Array<{ locale?: string, pdfFile?: string }>,
 *   spacing?: string, headingLevel?: number, bgColor?: string, textColor?: string,
 *   buttonBgColor?: string, buttonTextColor?: string,
 * }} props
 */
export function MenuDownloadBlock({
  title, subtitle, buttonLabel, items = [], spacing = 'normal', headingLevel = 2,
  bgColor = '', textColor = '', buttonBgColor = '', buttonTextColor = '',
}) {
  const { locale } = useI18n();
  const [open, setOpen] = useState(false);
  const item = items.find((i) => i.locale === locale) || items[0];

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  if (!item?.pdfFile) return null;

  const label = buttonLabel || FALLBACK_LABEL[locale] || FALLBACK_LABEL.it;

  function handleClick(e) {
    if (window.matchMedia('(max-width: 768px)').matches) return;
    e.preventDefault();
    setOpen(true);
  }

  return (
    <section
      className={'px-6 md:px-16 text-center text-theme-text ' + spacingClass(spacing)}
      style={{ backgroundColor: bgColor || undefined, color: textColor || undefined }}
    >
      {title && <Heading level={headingLevel} className="font-heading text-h1 font-light mb-4">{title}</Heading>}
      {subtitle && <p className="text-body-lg leading-relaxed mb-9 max-w-md mx-auto opacity-70">{subtitle}</p>}

      <a
        href={item.pdfFile}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleClick}
        style={{
          backgroundColor: buttonBgColor || 'var(--color-accent)',
          color: buttonTextColor || 'var(--color-accent-text)',
        }}
        className="inline-flex items-center gap-2.5 px-8 py-3.5 rounded-full text-sm font-medium tracking-wide transition-transform hover:scale-[1.02]"
      >
        <FileText size={16} />
        {label}
      </a>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={label}
          className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 md:p-10"
          onClick={() => setOpen(false)}
        >
          <div
            className="relative w-full h-full max-w-3xl bg-white rounded-xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Chiudi"
              className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80 transition-colors"
            >
              <X size={18} />
            </button>
            <iframe src={item.pdfFile} title={label} className="w-full h-full border-0" />
          </div>
        </div>
      )}
    </section>
  );
}
